import _keyBy from 'lodash/keyBy';
import { Team, User, UserStatus, UserType } from 'api/models';
import { TimeOffType, TimeoffResponse } from 'api/timeoff/types';
import { db } from './db';
import { Team as TeamModel } from './models';

export async function saveMemberData(members: User[]): Promise<number> {
  // clear existing members data before inserting
  await db.members.clear();

  return db.members.bulkPut(members);
}

export async function saveTeamData(teams: Team[]): Promise<number> {
  // clear existing team data before inserting
  await db.teams.clear();

  return db.teams.bulkPut(teams);
}

export async function addMember(member: User): Promise<number> {
  const user = await db.members.get(member.userId);

  // activating an deactivated user will also trigger the addMember handler
  // in that case just update the user data to not loose the existing projectIds prop
  // Note: projectIds prop is not sent in user object which comes via web socket
  if (user) {
    return db.members.update(member.userId, { ...user, ...member });
  }

  return db.members.put({ ...member, projectIds: [] });
}

export async function updateMember(member: User): Promise<unknown> {
  const user = await db.members.get(member.userId);

  if (user) {
    return db.members.update(member.userId, { ...user, ...member });
  }

  return Promise.reject(new Error(`No user found for id ${member?.userId}`));
}

export async function deleteMember(userId: number): Promise<void> {
  return db.members.delete(userId);
}

export async function addTeam(team: Team): Promise<number> {
  const teamdata = new TeamModel(team);

  return db.teams.put(teamdata);
}

export async function updateMemberStatus(
  userId: number,
  userStatus: UserStatus,
): Promise<number> {
  return db.members.update(userId, {
    userStatus,
  });
}

export async function updateTeam(team: Team): Promise<void> {
  try {
    const teamData = await db.teams.get(team.teamId);

    await db.teams.update(team.teamId, {
      ...team,
      projectIds: teamData?.projectIds,
      accountLevelTeam: teamData?.accountLevelTeam,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    throw new Error(
      `Error occured while updatimg team. teamId: ${team?.teamId}`,
      {
        cause: error,
      },
    );
  }
}

export async function deleteTeam(teamId: number): Promise<void> {
  return db.teams.delete(teamId);
}

export async function getTeam(teamId: number): Promise<Team | undefined> {
  return db.teams.get(teamId);
}

export async function addProjectToTeam({
  teamId,
  projectId,
}: {
  teamId: number;
  projectId: number;
}): Promise<void> {
  try {
    const team = await db.teams.get(teamId);

    if (team && !team?.projectIds?.includes(projectId)) {
      team?.projectIds?.push(projectId);
      await db.teams.update(teamId, team);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    throw new Error(
      `Error occured while adding projectId to team. teamId: ${teamId} projectId: ${projectId}`,
      {
        cause: error,
      },
    );
  }
}
export async function addProjectToTeams({
  teamIds,
  projectId,
}: {
  teamIds: number[];
  projectId: number;
}): Promise<void> {
  teamIds.forEach(async (teamId) => {
    try {
      const team = await db.teams.get(teamId);

      if (team && !team?.projectIds?.includes(projectId)) {
        team?.projectIds?.push(projectId);
        await db.teams.update(teamId, team);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      throw new Error(
        `Error occured while adding projectId to team. teamId: ${teamId} projectId: ${projectId}`,
        {
          cause: error,
        },
      );
    }
  });
}
export async function removeProjectFromTeam({
  teamId,
  projectId,
}: {
  teamId: number;
  projectId: number;
}): Promise<void> {
  try {
    const team = await db.teams.get(teamId);

    if (team && team?.projectIds?.includes(projectId)) {
      team.projectIds = team?.projectIds?.filter((p) => p !== projectId);
      await db.teams.update(teamId, team);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    throw new Error(
      `Error occured while removing projectId from team. teamId: ${teamId} projectId: ${projectId}`,
      {
        cause: error,
      },
    );
  }
}

export async function removeUserFromProject(
  userId: number,
  projectId: number,
): Promise<number> {
  const user = await db.members.get(userId);

  const projectIds = user?.projectIds?.filter((p) => p !== projectId);

  const res = await db.members.update(userId, {
    projectIds,
  });

  return res;
}

export async function addUserToProject(
  userId: number,
  projectId: number,
): Promise<number | undefined> {
  const user = await db.members.get(userId);

  const projectIds = user?.projectIds;

  if (user) {
    if (!projectIds?.includes(projectId)) {
      projectIds?.push(projectId);
    }

    await db.members.update(userId, {
      projectIds,
    });
  }

  return undefined;
}

export async function addUsersAndTeamsToNewProject({
  projectId,
  userIds,
  teams,
}: {
  userIds: number[];
  projectId: number;
  teams: Team[];
}): Promise<boolean> {
  try {
    // check if project already added to users
    const projectExists = await db.members
      .where('projectIds')
      .equals(projectId)
      .toArray();

    // if atleast one user already exist in the project return and save the below computation
    if (projectExists?.length) {
      return false;
    }

    const users = await db.members.where('userId').anyOf(userIds).toArray();

    if (users?.length) {
      users.forEach((user) => {
        if (!user.projectIds?.includes(projectId)) {
          user.projectIds?.push(projectId);
        }
      });

      const teamList = teams.map((team) => ({
        ...team,
        projectIds: [projectId],
      }));

      await Promise.all([
        db.teams.bulkPut(teamList),
        db.members.bulkPut(users),
      ]);

      return true;
    }
  } catch (error) {
    throw new Error(
      `Error occured while updating newly created projectId:${projectId} in users and teamss`,
      {
        cause: error,
      },
    );
  }

  return false;
}

export async function addUserTimeoff({
  userId,
  timeoffData,
}: {
  userId: number;
  timeoffData: TimeoffResponse;
}): Promise<number | undefined> {
  try {
    const user = await db.members.get(userId);

    const timeOff = user?.userTimeOffDetails
      ? [...user?.userTimeOffDetails]
      : [];

    timeOff.push({
      note: timeoffData?.note || '',
      timeOffEnd: timeoffData?.timeOffEnd || '',
      timeOffId: timeoffData?.timeOffId,
      timeOffStart: timeoffData?.timeOffStart || '',
      timeOffDuration: timeoffData?.timeOffDuration,
      timeOffType: timeoffData?.timeOffType || TimeOffType.FullDay,
      appliedBy: timeoffData?.appliedBy,
    });

    if (user) {
      await db.members.update(userId, {
        userTimeOffDetails: timeOff,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return undefined;
}
export async function deleteUserTimeoff({
  userId,
  timeoffId,
}: {
  userId: number;
  timeoffId: number;
}): Promise<number | undefined> {
  try {
    const user = await db.members.get(userId);

    const filtertedTimeoffData = user?.userTimeOffDetails?.filter(
      (t) => t.timeOffId !== timeoffId,
    );

    if (user) {
      await db.members.update(userId, {
        userTimeOffDetails: filtertedTimeoffData,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return undefined;
}

export async function getUsersInProject(
  projectId: number,
): Promise<Array<User>> {
  return db.members
    .where('projectIds')
    .equals(projectId)
    .filter((user) => user.userStatus !== UserStatus.Inactive)
    .toArray();
}
export async function getUsersInProjects(
  projectIds: number[],
): Promise<Array<User>> {
  return db.members
    .where('projectIds')
    .anyOf(projectIds)
    .filter((user) => user.userStatus !== UserStatus.Inactive)
    .toArray();
}

export async function getUsersNotInProject(
  projectId: number,
): Promise<Array<User>> {
  const usersNotinProject = await db.members
    .filter(
      (user) =>
        !user.projectIds?.includes(projectId) &&
        user.userStatus !== UserStatus.Inactive,
    )
    .toArray();

  return usersNotinProject;
}

export async function getActiveUsers(): Promise<Array<User>> {
  const activeUsers = await db.members
    .filter((user) => user.userStatus !== UserStatus.Inactive)
    .toArray();

  return activeUsers;
}

export async function getAllActiveUsersAndTeams(): Promise<{
  activeUsers: Array<User>;
  teams: Array<Team>;
}> {
  const activeUsers = await getActiveUsers();
  const teams = await db.teams.toArray();

  return { activeUsers, teams };
}

export async function getAllNativeUsers(): Promise<Array<User>> {
  const activeUsers = await db.members
    .where({
      userType: UserType.Native,
    })
    .filter((user) => user.userStatus !== UserStatus.Inactive)
    .toArray();

  return activeUsers;
}

export async function getUserById(
  userId?: number,
): Promise<User | Record<string, unknown>> {
  if (!userId) {
    return {};
  }

  const user = await db.members
    .where({
      userId,
    })
    .first();

  return user || ({} as User);
}

export async function getUsersByIds(
  userIds: number[],
): Promise<Record<number, User>> {
  if (!userIds.length) {
    return [];
  }

  const users = await db.members.where('userId').anyOf(userIds).toArray();

  return _keyBy(users, 'userId');
}

export async function getAllUsers(): Promise<Record<number, User>> {
  const users = await db.members.toArray();

  return _keyBy(users, 'userId');
}

export async function updateUserHolidayCalendar({
  userId,
  holidayCalendarId,
}: {
  userId: number;
  holidayCalendarId: number;
}): Promise<number | undefined> {
  try {
    const user = await db.members.get(userId);

    if (user) {
      await db.members.update(userId, {
        ...user,
        holidayCalendarId,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return undefined;
}

export async function addUserHolidayCalendar({
  emails,
  holidayCalendarId,
}: {
  emails: string[];
  holidayCalendarId: number;
}): Promise<number | undefined> {
  try {
    const users = await db.members.where('emailId').anyOf(emails).toArray();

    if (users) {
      users.forEach(async (user) => {
        await db.members.update(user.userId, {
          ...user,
          holidayCalendarId,
        });
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return undefined;
}
